// SPEC-29 · a 0–10 answer as the design system's ScaleSelector, "track"
// variant: the chosen number in a large numeral, eleven pills she taps or
// drags along, the row of numbers under them and the words at both ends.
// The lib's item list gives the bounds; the copy gives the words. The
// value is the whole number under her finger, nothing more.

import { useRef } from "react";
import type { KeyboardEvent, PointerEvent } from "react";

import { copy } from "../i18n/pt-PT";

const text = copy.questionnaire;

/** Violet by default, Orange for a symptom's intensity (SPEC-20). */
export const SCALE_ACCENTS = {
  violet: { fill: "bg-violet", tint: "bg-violet-tint", ink: "text-violet-ink" },
  orange: { fill: "bg-orange", tint: "bg-orange-tint", ink: "text-orange-ink" },
} as const;

export type ScaleAccent = keyof typeof SCALE_ACCENTS;

// Every pill is 44 px tall and a resting one is squeezed to 32 px with a
// scale, so a step grows without a layout pass.
const motion = "transition-[scale,background-color] duration-[var(--dur-micro)] ease-pyf motion-reduce:transition-none";
const RESTING = "scale-y-[calc(32/44)]";

export function ScaleSelector({
  min,
  max,
  value,
  onChange,
  label,
  ends,
  accent,
}: {
  min: number;
  max: number;
  value: number | undefined;
  onChange: (value: number) => void;
  /** What the slider is called for a screen reader: the question or the block. */
  label: string;
  ends: { min: string; max: string } | undefined;
  accent: ScaleAccent;
}) {
  const track = useRef<HTMLDivElement>(null);
  const colours = SCALE_ACCENTS[accent];
  const steps = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  const columns = { gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` };
  const clamp = (n: number) => Math.min(max, Math.max(min, n));

  /** The step under the finger: by position on the track, or by the pill itself where there is no layout. */
  const stepAt = (event: PointerEvent<HTMLDivElement>): number | undefined => {
    const box = track.current?.getBoundingClientRect();
    if (box && box.width > 0 && Number.isFinite(event.clientX)) return clamp(min + Math.floor(((event.clientX - box.left) / box.width) * steps.length));
    const step = (event.target as HTMLElement).closest<HTMLElement>("[data-step]")?.dataset.step;
    return step === undefined ? undefined : Number(step);
  };

  const set = (step: number | undefined) => {
    if (step !== undefined && step !== value) onChange(step);
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    set(stepAt(event));
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) set(stepAt(event));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const by = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[event.key];
    if (by === undefined) return;
    event.preventDefault();
    set(value === undefined ? min : clamp(value + by));
  };

  return (
    <div className="flex flex-col gap-4 rounded-card border border-line-10 bg-white p-[var(--card-padding)] select-none">
      <div className="flex items-end justify-between gap-4">
        <span className="text-body font-semibold text-muted">{value === undefined ? text.scaleIdle : ""}</span>
        <p aria-hidden="true" className="text-display-xl leading-[0.85] font-medium tracking-[var(--ls-heading)]">
          <span className={value === undefined ? "text-line-20" : "text-dark"}>{value ?? text.noNumber}</span>
          <span className={`text-[length:var(--numeral-unit)] ${colours.ink}`}>/{max}</span>
        </p>
      </div>

      <div
        ref={track}
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        {...(value !== undefined && { "aria-valuenow": value })}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onKeyDown={onKeyDown}
        className="grid h-[var(--touch-min)] cursor-pointer touch-none items-center gap-1 rounded-pill outline-none focus-visible:ring-2 focus-visible:ring-violet"
        style={columns}
      >
        {steps.map((step) => {
          const current = step === value;
          const filled = value !== undefined && step <= value;
          return (
            <span
              key={step}
              data-step={step}
              className={`flex items-center justify-center rounded-pill ${motion} h-[var(--touch-min)] ${current ? "" : RESTING} ${filled ? colours.fill : colours.tint}`}
            >
              {current && <span className="size-2 rounded-pill bg-white" />}
            </span>
          );
        })}
      </div>

      <div aria-hidden="true" className="-mt-2 grid gap-1 text-center text-label max-[359px]:text-[10px]" style={columns}>
        {steps.map((step) => (
          <span key={step} className={step === value ? `font-semibold ${colours.ink}` : "text-muted"}>
            {step}
          </span>
        ))}
      </div>

      {ends && (
        <div className="flex justify-between gap-4 border-t border-line-8 pt-3 text-caption-sm text-muted">
          <span>{`${min} · ${ends.min}`}</span>
          <span className="text-right">{`${ends.max} · ${max}`}</span>
        </div>
      )}
    </div>
  );
}
